/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { NOT_APPLICABLE } from '@config/constants/common';
import { useValidImage } from '@hooks/useValidImage';
import {
	Avatar,
	Box,
	Card,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Tooltip,
} from '@mantine/core';
import { IconQuestionMark } from '@tabler/icons-react';
import { getCurrencyUnit } from '@utils/currency-units';
import { truncateString } from '@utils/functions';
import {
	ChangeEventHandler,
	MouseEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { IconProjectLocation } from '../IconProjectLocation/IconProjectLocation';
import { StatusSwitch } from '../StatusSwitch';
import { Text } from '../Text';
import { Title } from '../Title';
import classes from './AdditionalityProjectCard.module.css';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

export type TProjectCardProps = {
	projectType: string | undefined;
	name: string;
	cover: string | undefined;
	location: {
		countryCode: string;
		place: string;
		countryName: string;
	};
	active: boolean;
	onStatusChange:
		| ChangeEventHandler<HTMLInputElement>
		| undefined;
	fullWidth?: boolean;
	supportedByLogo: string | undefined;
	fundedPortion: number;
	onClick: MouseEventHandler<HTMLDivElement> | undefined;
	showStatus: boolean;
	readonly?: boolean;
	showPrice?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AdditionalityProjectCard: React.FC<
	TProjectCardProps
> = ({
	name,
	active,
	onStatusChange,
	fullWidth,
	cover,
	supportedByLogo,
	location,
	projectType,
	fundedPortion,
	onClick,
	showStatus = true,
	readonly,
	showPrice,
}) => {
	const { t } = useTranslation();

	const coverUrl = useValidImage(cover);
	/* ******************************** RENDERING ******************************* */
	return (
		<Card
			shadow="sm"
			pl={'var(--mantine-spacing-xxs)'}
			pr={'var(--mantine-spacing-xxs)'}
			pt={'var(--mantine-spacing-xxs)'}
			pb={'var(--mantine-spacing-sm)'}
			radius="var(--mantine-radius-lg)"
			withBorder
			miw={'300px'}
			maw={fullWidth ? undefined : '300px'}
			flex={fullWidth ? 1 : 0}
			className={classes.container}
			onClick={onClick}
		>
			<Box
				h={203}
				p={'var(--mantine-spacing-xs)'}
				style={{
					borderRadius: '24px',
					position: 'relative',
					background: `lightgray url(${coverUrl}) 50% / cover no-repeat`,
				}}
			>
				<Flex
					justify={
						supportedByLogo ? 'space-between' : 'flex-end'
					}
				>
					<img
						src={cover}
						alt={'alt'}
						style={{
							display: 'none',
						}}
					/>

					{supportedByLogo && (
						<Image
							radius={'xl'}
							src={supportedByLogo}
							h={48}
							w={48}
						/>
					)}
					<Box
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						{showStatus && (
							<StatusSwitch
								inActiveLabel={t('global.inactive')}
								activeLabel={t('global.active')}
								active={active}
								readonly={readonly}
								onChange={onStatusChange}
							/>
						)}
					</Box>
				</Flex>
			</Box>

			<Flex
				direction={'column'}
				mt="var(--mantine-spacing-sm)"
				pl={'var(--mantine-spacing-xxs)'}
				pr={'var(--mantine-spacing-xxs)'}
			>
				{/* Row 1 Project Address*/}
				<Flex
					gap={'var(--mantine-spacing-xs)'}
					mih={'20px'}
				>
					<Flex mih={'20px'}>
						<Group justify="center" gap={'5px'}>
							<Avatar
								radius={'xl'}
								src={`https://flagcdn.com/w40/${location?.countryCode.toLowerCase()}.png`}
								size={18}
							>
								<IconQuestionMark size={18} />
							</Avatar>
							<Tooltip
								label={location.countryName}
								disabled={
									location.countryName.length <= 11
								}
							>
								<Box>
									<Text
										variant={'caption'}
										c={
											'var(--mantine-color-igluSecondary-9)'
										}
									>
										{truncateString(
											location.countryName,
											12
										)}
									</Text>
								</Box>
							</Tooltip>
						</Group>
					</Flex>
					<Flex
						style={{
							minHeight: '20px',
						}}
					>
						<Group gap={'5px'}>
							<IconProjectLocation color={'#617EEF'} />
							<Tooltip
								label={location.place}
								disabled={location.place.length <= 11}
							>
								<Box>
									<Text
										variant={'caption'}
										c={
											'var(--mantine-color-igluSecondary-9)'
										}
									>
										{truncateString(location.place, 12)}
									</Text>
								</Box>
							</Tooltip>
						</Group>
					</Flex>
					{showPrice && (
						<Flex flex={1} justify={'end'} mih={'20px'}>
							<Text
								variant={'caption'}
								c={'var(--mantine-color-igluSecondary-9)'}
							>
								{t('global.fundedPortion')}
							</Text>
						</Flex>
					)}
				</Flex>
				{/* Row 2 Project Value*/}
				{showPrice && (
					<Flex gap={'var(--mantine-spacing-xs)'}>
						<Flex flex={1} justify={'end'} mih={'20px'}>
							<Title
								order={6}
								c={'var(--mantine-color-igluPrimary-9)'}
							>
								<NumberFormatter
									thousandSeparator=" "
									decimalSeparator=","
									value={fundedPortion}
									decimalScale={2}
								/>
								<Text
									span
									variant={'caption'}
									c={'var(--mantine-color-igluPrimary-9)'}
								>
									{getCurrencyUnit('EUR')}
								</Text>
							</Title>
						</Flex>
					</Flex>
				)}
				{/* Row 3 Project label*/}
				<Flex
					mt={'var(--mantine-spacing-xs)'}
					gap={'var(--mantine-spacing-xs)'}
				>
					<Flex flex={1} mih={'20px'}>
						<Text
							variant="caption"
							c={'var(--mantine-color-igluSecondary-9)'}
						>
							{t('placeholders.projectName')}
						</Text>
					</Flex>
				</Flex>
				{/* Row 4 Project name*/}
				<Flex gap={'var(--mantine-spacing-xs)'}>
					<Flex flex={1} mih={'20px'}>
						<Tooltip
							label={name}
							disabled={name ? name.length <= 29 : true}
						>
							<Box>
								<Text
									variant="subtitle1"
									c={
										'var(--mantine-color-igluTextPrimary-9)'
									}
								>
									{(name && truncateString(name, 30)) ||
										NOT_APPLICABLE}
								</Text>
							</Box>
						</Tooltip>
					</Flex>
				</Flex>
				{/* Row 5 Project type*/}
				<Flex
					gap={'var(--mantine-spacing-xs)'}
					mt={'var(--mantine-spacing-sm)'}
				>
					<Flex
						flex={1}
						style={{
							minHeight: '20px',
						}}
					>
						<Group gap={'5px'}>
							<Text
								variant={'caption'}
								c={'var(--mantine-color-igluSecondary-9)'}
							>
								{t(`global.projectType`)}
							</Text>
						</Group>
					</Flex>
					<Flex flex={1.5} mih={'20px'}>
						<Tooltip
							label={projectType && t(projectType)}
							disabled={
								projectType
									? t(projectType).length <= 23
									: true
							}
						>
							<Box>
								<Text
									variant={'body2'}
									c={
										'var(--mantine-color-igluTextPrimary-9)'
									}
								>
									{(projectType &&
										truncateString(t(projectType), 24)) ||
										NOT_APPLICABLE}
								</Text>
							</Box>
						</Tooltip>
					</Flex>
				</Flex>
				{/* Row 7 Button*/}
				<Button
					mt={'var(--mantine-spacing-md)'}
					variant={'primary'}
				>
					{t('global.learnMore')}
				</Button>
			</Flex>
		</Card>
	);
};
